import { lazy } from "react";

import AuthGuard from "./AuthGuard";
import SuperAdminAuthGuard from "./SuperAdminAuthGuard";
import PageRouter from "../pages/pageRouter/PageRouter";
import ComingSoon from "../components/comingSoon/ComingSoon";
import { GroupProvider } from "../data/groupContextApi";
import StatusBoard from "../pages/statusBoard/StatusBoard";
import StatusBoardColumn from "../pages/statusBoardColumn/StatusBoardColumn";
import { CareTeams } from "../pages/careTeams/CareTeams";
import { ReportsManagement } from "../pages/reportsManagement/ReportsManagement";
import { EmsReportManagement } from "../pages/emsReportManagement/EmsReportManagement";
import BedReport from "../pages/bedReport/BedReport";
import { ScheduleReport } from "../pages/scheduleReport/ScheduleReport";
import { DashBoardTable } from "../pages/dashboard/components/dashboardTable/DashBoardTable";
import { AssignNurse } from "../pages/assignNurse/AssignNurse";
import { PublicHolidays } from "../pages/publicHolidays/PublicHolidays";
import { Help } from "../pages/help/Help";
import EmptyPage from "../components/empty/EmptyPage";
import Spinner from "../components/spinner/Spinner";
import SystemReports from "../pages/systemReports/systemReports";
import Training from "../pages/training/Training";
import { RequestLists } from "../pages/SuperAdmin/requestLists/RequestLists";
import { ONAddUser } from "../pages/SuperAdmin/requestLists/components";
import { NonMembersList } from "../pages/SuperAdmin/memberManagement/nonMembersList";
import { ActiveList } from "../pages/SuperAdmin/accountsManagement/activeLists";
import { MembershipPlanManagement } from "../pages/SuperAdmin/membershipPlanManagement/MembershipPlanManagement";
import { UploadUsers } from "../pages/SuperAdmin/requestLists/components/uploadUsers/UploadUsers";
import { CreateNetwork } from "../pages/SuperAdmin/createNetwork/createNetwork";
import { DNMembershipManagement } from "../pages/SuperAdmin/dnMembershipManagement/dnMembershipMangement";
import { NetworkMangement } from "../pages/SuperAdmin/networkMangement/networkMangement";
import { SAPNetworkMangement } from "../pages/SuperAdmin/SAPNetworkManagment/SAPNetworkManagement";
import { BusinessAccountLists } from "../pages/SuperAdmin/BusinessAccountManagement/businessaccountmanagement";
import PaymentSuccess from "../pages/SuperAdmin/createNetwork/paymentSuccess";
import PaymentFailed from "../pages/SuperAdmin/createNetwork/paymentFailed";
import CommunicationModule from "../pages/SuperAdmin/communicationModule/communicationModule";
import BulkEmailPage from "../pages/SuperAdmin/BulkEmail/BulkEmail";
import BACommunicationModule from "../pages/SuperAdmin/BACommunicationModule/BACommunicationModule";
import SAPBACommunicationChatNew from "../pages/SuperAdmin/SAPBACommunication/SAPBACommunicationNew";

const LandingPage = lazy(() => import("../pages/landingPage/LandingPage"));
const LoginForm = lazy(() => import("../pages/login/LoginForm"));
const VerifyEmail = lazy(() => import("../pages/login/components/verifyEmail/VerifyEmail"));
const LoginRecover = lazy(() => import("../pages/login/components/forgotPassword/LoginRecover"));
const ResetPassword = lazy(() => import("../pages/login/components/forgotPassword/ResetPassword"));

const Home = lazy(() => import("../pages/home/Home"));

const User = lazy(() => import("../pages/user/User"));
const AddUser = lazy(() => import("../pages/user/components/addUser/AddUser"));
const UploadFile = lazy(() => import("../pages/user/components/uploadFile/UploadFile"));
const UserRule = lazy(() => import("../pages/userRules/UserRule"));

const AddSites = lazy(() => import("../pages/sites/components/addSites/AddSites"));
const ViewSite = lazy(() => import("../pages/sites/components/viewSite/ViewSite"));
const Sites = lazy(() => import("../pages/sites/Sites"));

const Speciality = lazy(() => import("../pages/speciality/Speciality"));
const AddSpeciality = lazy(() => import("../pages/speciality/components/addSpeciality/components/AddSpeciality"));

const AddRole = lazy(() => import("../pages/roles/components/addRole/AddRole"));
const RoleChange = lazy(() => import("../pages/roles/components/roleChange/RoleChange"));
const AddRoleBasedPermission = lazy(() =>
  import("../pages/roles/components/roleChange/components/components/AddRoleBasedPermission")
);

const Group = lazy(() => import("../pages/group/Group"));
const AddUserGroup = lazy(() => import("../pages/group/components/addGroup/AddUserGroup"));
const UnAssignedShiftTable = lazy(() =>
  import("../pages/group/components/groupSchedule/components/UnAssignedShiftTable")
);
const UnAssignedShiftRequestCover = lazy(() =>
  import("../pages/group/components/groupSchedule/components/UnAssignedShiftRequestCover")
);
const AddGroup = lazy(() => import("../pages/group/components/addGroup/AddGroupCard"));
const AddSchedule = lazy(() => import("../pages/group/components/addSchedule/AddScheduleCard"));
const Shift = lazy(() => import("../pages/shifts/Shift"));
const Scheduler = lazy(() => import("../pages/shifts/scheduler/Scheduler"));
const ShiftsOverview = lazy(() => import("../pages/shifts/ShiftsOverview"));

const UnitDepartments = lazy(() => import("../pages/unitDepartments/UnitDepartments"));

const SupportStaff = lazy(() => import("../pages/supportStaff/SupportStaff"));
const Dashboard = lazy(() => import("../pages/dashboard/Dashboard"));

export const routes = [
  {
    path: "/superadmin/login",
    element: <LandingPage />,
    children: [
      {
        path: "",
        element: <LoginForm isSuperAdmin={true} />,
      },
    ],
  },
  {
    // Public -- reached from the business-account verification email before
    // the person has any session, so this must sit outside SuperAdminAuthGuard.
    path: "/superadmin/verify-email",
    element: <VerifyEmail />,
  },
  {
    path: "/superadmin",
    errorElement: <ComingSoon />,
    element: (
      <SuperAdminAuthGuard>
        <GroupProvider>
          <PageRouter isSuperAdmin={true} />
        </GroupProvider>
      </SuperAdminAuthGuard>
    ),
    children: [
      {
        path: "users",
        element: <ComingSoon />,
      },
      {
        path: "members",
        element: <ComingSoon />,
      },
      {
        path: "deletedusers",
        element: <ComingSoon />,
      },
      {
        path: "request-lists",
        element: <RequestLists />,
      },
      {
        path: "member-lists",
        element: <NonMembersList />,
      },

      {
        path: "account-lists",
        element: <ActiveList />,
      },
      {
        path: "account-lists/edit-user",
        element: <ONAddUser />,
      },
      {
        path: "request-lists/add-user",
        element: <ONAddUser />,
      },
      {
        path: "request-lists/upload-users",
        element: <UploadUsers />,
      },
      {
        path: "memebership-management",
        element: <MembershipPlanManagement />,
      },
      {
        path: "create-network",
        element: <CreateNetwork />,
      },
      {
        path: "subscription-plan",
        element: <DNMembershipManagement />,
      },
      {
        path: "communication",
        element: <CommunicationModule />,

      },

         {
        path: "sap-communication",
        element: <BACommunicationModule />,

      },

      {
        path: "communication/bulk-email",
        element: <BulkEmailPage />,
      },

      
      {
        path: "communication/BAcommunication",
        element: <SAPBACommunicationChatNew />,
      },
      {
        path: "network-listing",
        element: <NetworkMangement />,
      },


      {
        path: "sap-network-management",
        element: <SAPNetworkMangement />,
      },

      {
        path: "business-account-management",
        element: <BusinessAccountLists />,
      },
      {
        path: "payment-success",
        element: <PaymentSuccess />,
      },
      {
        path: "payment-cancel",
        element: <PaymentFailed />,
      },

    ],
  },
  {
    path: "/login",
    element: <LandingPage />,
    children: [
      {
        path: "",
        element: <LoginForm isSuperAdmin={false} />,
      },
    ],
  },
  {
    path: "/forgot-password",
    element: <LandingPage />,
    children: [
      {
        path: "",
        element: <LoginRecover />,
      },
    ],
  },
  {
    path: "/forgot-username",
    element: <LandingPage />,
    children: [
      {
        path: "",
        element: <LoginRecover />,
      },
    ],
  },
  {
    path: "/superadmin/forgot-password",
    element: <LandingPage />,
    children: [
      {
        path: "",
        element: <LoginRecover isSuperAdmin={true} />,
      },
    ],
  },
  {
    path: "/superadmin/forgot-username",
    element: <LandingPage />,
    children: [
      {
        path: "",
        element: <LoginRecover isSuperAdmin={true} />,
      },
    ],
  },
  {
    path: "/superadmin/auth/reset-password/:token",
    element: <LandingPage />,
    children: [
      {
        path: "",
        element: <ResetPassword isSuperAdmin={true} />,
      },
    ],
  },
  {
    path: "/auth/reset-password/:token/:code",
    element: <LandingPage />,
    children: [
      {
        path: "",
        element: <ResetPassword />,
      },
    ],
  },





  {
    path: "/",
    errorElement: <ComingSoon />,
    element: (
      <AuthGuard>
        <GroupProvider>
          <PageRouter />
        </GroupProvider>
      </AuthGuard>
    ),

    children: [
      {
        path: "home",
        element: <Home />,
      },
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "dashboard/providers",
        element: <DashBoardTable />,
      },
      {
        path: "users",
        element: <User />,
      },
      {
        path: "groups",
        element: <Group />,
      },
      {
        path: "shift",
        element: <Shift />,
      },
      {
        path: "shift/:scheduleId",
        element: <Shift />,
      },
      {
        path: "sites",
        element: <Sites />,
      },
      {
        path: "speciality",
        element: <Speciality />,
      },
      {
        path: "units-departments",
        element: <UnitDepartments />,
      },
      {
        path: "assign-nurse",
        element: <AssignNurse />,
      },
      {
        path: "support-staffs",
        element: <SupportStaff />,
      },
      {
        path: "care-teams",
        element: <CareTeams />,
      },
      {
        path: "status-board-columns",
        element: <StatusBoardColumn />,
      },
      {
        path: "status-boards",
        element: <StatusBoard />,
      },
      {
        path: "bed-report",
        element: <ComingSoon />,
      },
      {
        path: "sites/add-sites",
        element: <AddSites />,
      },
      {
        path: "sites/edit-sites/:siteId?",
        element: <AddSites />,
      },
      {
        path: "sites/view-sites/:siteId",
        element: <ViewSite />,
      },
      {
        path: "add-speciality",
        element: <AddSpeciality />,
      },
      {
        path: "edit-speciality/:specialityId?/:roleName?",
        element: <AddSpeciality />,
      },
      {
        path: "roles",
        element: <RoleChange />,
      },
      {
        path: "add-role",
        element: <AddRole />,
      },
      {
        path: "roles/edit-role/:roleID",
        element: <AddRole />,
      },
      {
        path: "add-role-based-permission",
        element: <AddRoleBasedPermission />,
      },
      {
        path: "edit-role-based-permission/:rolebasedId?",
        element: <AddRoleBasedPermission />,
      },
      {
        path: "users/add-user",
        element: <AddUser />,
      },
      {
        path: "users/edit-user/:userId?",
        element: <AddUser />,
      },
      {
        path: "users/upload-file",
        element: <UploadFile />,
      },
      {
        path: "*",
        element: <ComingSoon />,
      },
      {
        path: "groups/add-group",
        element: <AddGroup />,
      },
      {
        path: "groups/edit-group/:groupId",
        element: <AddGroup />,
      },
      {
        path: "groups/update-user/:groupIdToUpdateUser",
        element: <AddGroup />,
      },
      {
        path: "groups/add-schedule",
        element: <AddSchedule />,
      },
      {
        path: "groups/add-user",
        element: <AddUserGroup />,
      },
      {
        // Top-level entry for the sidebar's `shifts` permission. The sidebar
        // derives its path from the permission key by swapping underscores for
        // hyphens, so key `shifts` must be reachable at exactly "/shifts".
        path: "shifts",
        element: <ShiftsOverview />,
      },
      {
        path: "shift/scheduler",
        element: <Scheduler />,
      },
      {
        path: "shift/scheduler/:schedulerId",
        element: <Scheduler />,
      },
      {
        path: "users/user-rule/:userId?",
        element: <UserRule />,
      },
      {
        path: "groups/assign-user/:shiftId?",
        element: <UnAssignedShiftTable />,
      },
      {
        path: "groups/req-cover/:shiftId?",
        element: <UnAssignedShiftRequestCover />,
      },
      {
        path: "reports-management",
        element: <ReportsManagement />,
      },
      {
        path: "reports-management/ems-managment-report",
        element: <EmsReportManagement />,
      },
      {
        path: "reports-management/bed-report",
        element: <BedReport />,
      },
      {
        path: "reports-management/schedule-reports",
        element: <ScheduleReport />,
      },
      {
        path: "reports-management/system-reports",
        element: <SystemReports />,
      },
      {
        path: "public-holidays",
        element: <PublicHolidays />,
      },
      {
        path: "help",
        element: <Help />,
      },
      {
        path: "no-permission",
        element: <EmptyPage />,
      },
      {
        path: "",
        element: <Spinner />,
      },
      {
        path: "chatbot-training",
        element: <Training />,
      },
    ],
  },
];
